// «Повторить движение» (09.10, владелец: «использовать референсное видео, чтобы собрать тренд»): зона ролика с
// движением в шаге «Видео» — сам ролик, раскадровка с отрезком (ручки начала и конца, перетаскивание всего отрезка),
// длина и пределы Kling простыми словами. Отрезок — целые секунды (так спишет сервер), начало — с шагом 0,1 с.
// Режет ролик сервер при запуске ровно по этим секундам (scene_studio._start_repeat); здесь только выбор.
// 11.10: блок ① «Ролик» шота повтора — заголовок даёт блок (bare); до загрузки — каким должен быть ролик (значками, по
// правилам нейросети режима), после — что мы проверили сами, без нейросети и денег: формат и размер кадра.
import { useCallback, useEffect, useRef, useState, type DragEvent, type PointerEvent as ReactPointerEvent } from 'react'
import { AlertTriangle, Check, CloudUpload, PersonStanding, Scan, ScanFace, Scissors, Sun, Timer, Upload, UserRound, Users, Video, X } from 'lucide-react'
import { REPEAT_MIN_SECONDS, refVideoIdentity, repeatLength, type RefVideo } from '../../../api/scene'
import { Icon } from '../../../../public/icons/Icon'
import FilmFrame from './FilmFrame'
import { useFilmstrip } from './filmstrip'
import { clock, seconds as secondsLabel } from '../format'
import { clipChecks } from '../clipChecks'

const FRAMES = 10

/** Условие хорошего ролика — значком и словами. Набор задаёт вызывающий (в генераторе — по нейросети), без него — свой у зоны. */
export interface ClipReq {
	icon: 'user' | 'users' | 'face' | 'frame' | 'cut' | 'timer' | 'standing' | 'camera' | 'light'
	text: string
}
const REQ_ICONS: Record<ClipReq['icon'], typeof UserRound> = {
	user: UserRound, users: Users, face: ScanFace, frame: Scan, cut: Scissors, timer: Timer, standing: PersonStanding, camera: Video, light: Sun,
}

export default function MotionZone({ video, cap, min = REPEAT_MIN_SECONDS, replace = false, bare = false, lead, reqs, uploadTile = false, removeInSlot = false, warnOnly = false, plain = false, readonly, busy, shotVideo, onChange, onUpload, onUseShotVideo, onDropFile, onRemove, onMeta }: {
	video: RefVideo | null
	/** Предел длины отрезка при выбранной ориентации (10 или 30 с). */
	cap: number
	/** Самый короткий отрезок, который примет нейросеть (Kling — 3 с, «фон из ролика» — 2 с). */
	min?: number
	/** «Фон из ролика» (Wan Animate Replace): аватар встаёт на место человека в ролике — свои слова у зоны. */
	replace?: boolean
	/** Заголовок «Ролик» даёт блок ① вокруг зоны — своей подписи у зоны нет. */
	bare?: boolean
	/** Свои слова до загрузки (генератор: по нейросети режима); без них — текст по replace. */
	lead?: string
	/** Свои условия хорошего ролика (генератор: по нейросети режима); без них — набор зоны. */
	reqs?: ClipReq[]
	/** Загрузка слотом-плиткой на всё свободное место (генератор, владелец 13.10), а не кнопкой; перетаскивание — на всю зону. */
	uploadTile?: boolean
	/** Крестик — в углу превью ролика, без строки «Заменить» (генератор, владелец 13.10): заменить = убрать и загрузить снова. */
	removeInSlot?: boolean
	/** Из проверок ролика показывать только предупреждения: формат и размер в пикселях человеку не нужны (генератор). */
	warnOnly?: boolean
	/** Без имени файла и строки «Возьмём отрезок…» (генератор, владелец 13.10: «эта информация не нужна») — отрезок виден
	 *  на ленте и в строке времени; остаются только «ролик короче N с» и «узнаём длину». */
	plain?: boolean
	readonly: boolean
	/** Ролик загружается. */
	busy: boolean
	/** Готовое видео этого шота, которое можно взять роликом с движением (своя загрузка или результат). */
	shotVideo: { id: string; url: string; seconds: number | null } | null
	onChange: (patch: Partial<RefVideo>) => void
	onUpload: (button: HTMLElement) => void
	onUseShotVideo: () => void
	/** Файл перетащили на зону. */
	onDropFile: (file: File) => void
	onRemove: () => void
	/** Плеер узнал длину и пропорцию ролика (генератор рисует по ней рамку результата). */
	onMeta?: (meta: { seconds: number; aspect: number | null }) => void
}) {
	const url = video?.url ?? null
	const [drop, setDrop] = useState(false)
	// Длина и пропорция ролика — из плеера миниатюры. Длина сохранена — берём её; неизвестна (ролик из версии без длины)
	// или расходится с файлом — сохраняем ту, что у файла.
	const [meta, setMeta] = useState<{ key: string; seconds: number; aspect: number | null } | null>(null)
	const vkey = refVideoIdentity(video)
	const own = meta && meta.key === vkey ? meta : null
	const seconds = (Number(video?.seconds) > 0 ? Number(video?.seconds) : null) ?? own?.seconds ?? null
	const start = Math.max(0, Number(video?.start) || 0)
	const thumbRef = useRef<HTMLVideoElement>(null)
	const onLoaded = useCallback((e: React.SyntheticEvent<HTMLVideoElement>) => {
		const el = e.currentTarget
		const d = el.duration
		if (!Number.isFinite(d) || d <= 0) return
		const aspect = el.videoWidth > 0 && el.videoHeight > 0 ? el.videoWidth / el.videoHeight : null
		setMeta((m) => (m && m.key === vkey && m.seconds === d && m.aspect === aspect ? m : { key: vkey, seconds: d, aspect }))
		onMeta?.({ seconds: d, aspect })
		el.currentTime = Math.min(Math.max(0.05, start), Math.max(0, d - 0.05))
		if (readonly) return
		// Длина и размер кадра, которых не знал сервер (ролик из версии шота), — сохраняем: по длине считается цена,
		// по размеру кадр шота берёт форму ролика.
		const patch: Partial<RefVideo> = {}
		const known = Number(video?.seconds)
		if (!Number.isFinite(known) || Math.abs(known - d) > 0.25) patch.seconds = Math.round(d * 100) / 100
		if (el.videoWidth > 0 && el.videoHeight > 0 && (video?.width !== el.videoWidth || video?.height !== el.videoHeight)) {
			patch.width = el.videoWidth
			patch.height = el.videoHeight
		}
		if (Object.keys(patch).length) onChange(patch)
	}, [video?.seconds, video?.width, video?.height, vkey, readonly, onChange, onMeta, start])
	// Миниатюра показывает начало отрезка: двигаем сам плеер, а не адрес (иначе ролик качался бы заново на каждом шаге).
	useEffect(() => {
		const el = thumbRef.current
		if (el && el.readyState >= 1 && Number.isFinite(el.duration)) el.currentTime = Math.min(Math.max(0.05, start), Math.max(0, el.duration - 0.05))
	}, [start])

	const length = repeatLength(video ? { ...video, seconds } : null, cap, min)
	const total = seconds && seconds > 0 ? seconds : null
	const tooShort = total != null && total < min
	// Раскадровка — когда известна пропорция ролика: кадры рисуются в его форме (горизонтальный не сплющится).
	const aspect = own?.aspect ?? null
	const frames = useFilmstrip(url && aspect ? `motion:${vkey}` : null, aspect ? url : null, FRAMES, aspect ?? 1)

	// ── перетаскивание: ручки и весь отрезок ──
	const trackRef = useRef<HTMLDivElement>(null)
	const drag = useRef<{ kind: 'start' | 'end' | 'move'; x0: number; start0: number; len0: number; w: number } | null>(null)
	const commit = useCallback((nextStart: number, nextLen: number) => {
		if (!total) return
		const maxLen = Math.max(min, Math.min(cap, Math.floor(total + 0.05)))
		const len = Math.max(min, Math.min(maxLen, Math.round(nextLen)))
		const s = Math.round(Math.max(0, Math.min(total - len, nextStart)) * 10) / 10
		if (Math.abs(s - start) > 0.01 || len !== length) onChange({ start: s, length: len })
	}, [total, cap, min, start, length, onChange])
	const onDown = (kind: 'start' | 'end' | 'move') => (e: ReactPointerEvent<HTMLElement>) => {
		if (readonly || !total || !trackRef.current) return
		e.preventDefault()
		e.stopPropagation()
		const w = trackRef.current.getBoundingClientRect().width
		drag.current = { kind, x0: e.clientX, start0: start, len0: length, w }
		// Захват указателя — чтобы протяжка шла и за краем дорожки; не вышел (указатель уже отпущен) — тянем без него.
		try { e.currentTarget.setPointerCapture?.(e.pointerId) } catch { /* без захвата */ }
	}
	const onMove = (e: ReactPointerEvent<HTMLElement>) => {
		const d = drag.current
		if (!d || !total) return
		const dt = ((e.clientX - d.x0) / Math.max(1, d.w)) * total
		if (d.kind === 'move') commit(d.start0 + dt, d.len0)
		else if (d.kind === 'start') {
			// Начало двигается, конец стоит: длина меняется на столько же (в целых секундах).
			const end = d.start0 + d.len0
			const s = Math.max(0, Math.min(end - min, d.start0 + dt))
			commit(end - Math.round(end - s), Math.round(end - s))
		} else commit(d.start0, d.len0 + dt)
	}
	const onUp = (e: ReactPointerEvent<HTMLElement>) => {
		drag.current = null
		try { e.currentTarget.releasePointerCapture?.(e.pointerId) } catch { /* захвата не было */ }
	}
	// Клавиатура: стрелки двигают отрезок по 0,1 с (с Shift — по секунде), у ручки конца — длину по секунде.
	const onKey = (kind: 'move' | 'end') => (e: React.KeyboardEvent) => {
		if (readonly || !total) return
		const step = e.shiftKey ? 1 : 0.1
		if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
			e.preventDefault()
			const dir = e.key === 'ArrowRight' ? 1 : -1
			if (kind === 'move') commit(start + dir * step, length)
			else commit(start, length + dir)
		}
	}

	const dropProps = readonly ? {} : {
		onDragOver: (e: DragEvent<HTMLElement>) => {
			if (![...e.dataTransfer.types].includes('Files')) return
			e.preventDefault()
			e.dataTransfer.dropEffect = 'copy'
			if (!drop) setDrop(true)
		},
		onDragLeave: () => setDrop(false),
		onDrop: (e: DragEvent<HTMLElement>) => {
			e.preventDefault()
			setDrop(false)
			const f = [...e.dataTransfer.files].find((x) => x.type.startsWith('video/'))
			if (f) onDropFile(f)
		},
	}
	if (busy) {
		return (
			<div className={`sc-motion is-busy${bare ? ' is-bare' : ''}`} role="status">
				{!bare && <span className="sc-motion__label"><PersonStanding size={14} aria-hidden="true" /> Ролик</span>}
				<p className="sc-motion__wait"><Icon id="gen-spin" size={16} className="sc-spin" /> Загружаем ролик…</p>
			</div>
		)
	}

	if (!video?.url) {
		return (
			<div className={`sc-motion is-empty${bare ? ' is-bare' : ''}${drop ? ' is-drop' : ''}`} {...dropProps}>
				{!bare && <span className="sc-motion__label"><PersonStanding size={14} aria-hidden="true" /> Ролик</span>}
				<p className="sc-motion__lead">{lead ?? (replace
					? 'Танец или жест — аватар встанет на место человека, фон, свет и звук останутся. Лучше всего получается, когда:'
					: 'Танец или жест — аватар из кадра повторит его. Ролик выйдет на всю длину, когда:')}</p>
				{/* Каким должен быть ролик — по правилам нейросети режима (их отказы — services/error_messages): у «фона из
				    ролика» лицо ищется в каждом кадре, Kling нужен человек по пояс или целиком и неподвижная камера. Генератор
				    передаёт свой набор под выбранную нейросеть (владелец 13.10: условия — по нейросети, не одни на всех). */}
				<ul className="sc-motion__reqs" aria-label="Каким должен быть ролик">
					{reqs
						? reqs.map((r) => { const I = REQ_ICONS[r.icon]; return <li key={r.text}><I size={13} aria-hidden="true" />{r.text}</li> })
						: <>
							<li><UserRound size={13} aria-hidden="true" />один человек</li>
							{replace
								? <><li><ScanFace size={13} aria-hidden="true" />лицо видно всё время</li><li><Scan size={13} aria-hidden="true" />не выходит из кадра</li></>
								: <><li><PersonStanding size={13} aria-hidden="true" />по пояс или целиком</li><li><Video size={13} aria-hidden="true" />камера стоит</li></>}
							<li><Scissors size={13} aria-hidden="true" />без склеек</li>
							<li><Timer size={13} aria-hidden="true" />{min}–{cap} с</li>
						</>}
				</ul>
				{!readonly && uploadTile && (
					<button type="button" className="sc-motion__tile" aria-haspopup="menu" onClick={(e) => onUpload(e.currentTarget)}>
						<span className="sc-motion__tile-icon" aria-hidden="true"><CloudUpload size={20} strokeWidth={1.5} /></span>
						<span className="sc-motion__tile-label">Загрузить ролик</span>
					</button>
				)}
				{!readonly && !uploadTile && (
					<div className="sc-motion__acts">
						<button type="button" className="sc-btn sc-btn--sm" aria-haspopup="menu" onClick={(e) => onUpload(e.currentTarget)}><Upload size={14} /> Загрузить ролик</button>
						{shotVideo && <button type="button" className="sc-btn sc-btn--ghost sc-btn--sm" onClick={onUseShotVideo}>Взять видео этого шота</button>}
					</div>
				)}
			</div>
		)
	}

	const checks = clipChecks(video.width, video.height, replace).filter((c) => !warnOnly || c.tone === 'warn')
	const pct = (t: number) => (total ? `${Math.max(0, Math.min(100, (t / total) * 100))}%` : '0%')
	const end = start + length
	return (
		<div className={`sc-motion${bare ? ' is-bare' : ''}${drop ? ' is-drop' : ''}`} {...dropProps}>
			{!(bare && removeInSlot) && (
			<div className={`sc-motion__head${bare ? ' is-bare' : ''}`}>
				{!bare && <span className="sc-motion__label"><PersonStanding size={14} aria-hidden="true" /> Ролик</span>}
				{!readonly && (
					<span className="sc-motion__acts">
						<button type="button" className="sc-motion__ghost" aria-haspopup="menu" onClick={(e) => onUpload(e.currentTarget)}>Заменить</button>
						{!removeInSlot && <button type="button" className="sc-motion__x" aria-label="Убрать ролик" title="Убрать ролик" onClick={onRemove}><X size={13} /></button>}
					</span>
				)}
			</div>
			)}
			<div className="sc-motion__row">
				{/* Форма ролика — переменной: генератор рисует превью в его пропорции (без чёрных полей), студии размер задан стилем. */}
				<span className="sc-motion__thumb" style={aspect ? ({ '--clip-aspect': String(Math.round(aspect * 1000) / 1000) } as React.CSSProperties) : undefined}>
					<video ref={thumbRef} src={url ?? undefined} muted playsInline preload="metadata" onLoadedMetadata={onLoaded} aria-hidden="true" />
					{total != null && <span className="sc-motion__badge">{secondsLabel(total)}</span>}
					{!readonly && removeInSlot && (
						<button type="button" className="sc-motion__thumb-x" aria-label="Убрать ролик" title="Убрать ролик" onClick={onRemove}><X size={12} /></button>
					)}
				</span>
				<div className="sc-motion__info">
					{(() => {
						const meta = tooShort
							? `Ролик короче ${min} секунд — такой не подойдёт`
							: total == null ? 'Узнаём длину ролика…'
								: plain ? null : `${replace ? 'Возьмём' : 'Повторим'} отрезок ${clock(start)} – ${clock(Math.min(end, total))}`
						if (plain && !meta) return null
						return (
							<div>
								{!plain && <div className="sc-motion__name" title={video.name || undefined}>{video.name || 'Ваш ролик'}</div>}
								{meta && <div className="sc-motion__meta">{meta}</div>}
							</div>
						)
					})()}
					<div ref={trackRef} className={`sc-motion__track${readonly ? ' is-readonly' : ''}`} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
						<span className="sc-motion__frames" aria-hidden="true">{frames.map((f, i) => <FilmFrame key={i} frame={f} index={i} />)}</span>
						{total != null && !tooShort && (
							<>
								<span className="sc-motion__dim is-left" style={{ left: 0, width: pct(start) }} aria-hidden="true" />
								<span className="sc-motion__dim is-right" style={{ left: pct(end), right: 0 }} aria-hidden="true" />
								<span className="sc-motion__sel" style={{ left: pct(start), width: `calc(${pct(end)} - ${pct(start)})` }}
									role="slider" tabIndex={readonly ? -1 : 0} aria-label="Отрезок ролика: стрелки двигают начало, с Shift — по секунде"
									aria-valuemin={0} aria-valuemax={Math.max(0, total - length)} aria-valuenow={start} aria-valuetext={`с ${clock(start)}, ${length} с`}
									onPointerDown={onDown('move')} onKeyDown={onKey('move')} />
								<span className="sc-motion__handle" style={{ left: pct(start) }} onPointerDown={onDown('start')} aria-hidden="true" />
								<span className="sc-motion__handle is-end" style={{ left: pct(end) }} onPointerDown={onDown('end')}
									role="slider" tabIndex={readonly ? -1 : 0} aria-label="Длина отрезка, секунды" aria-valuemin={min} aria-valuemax={cap} aria-valuenow={length}
									onKeyDown={onKey('end')} />
							</>
						)}
					</div>
					<div className="sc-motion__time"><span>{clock(start)}</span><b>{length} с</b><span>{total != null ? clock(Math.min(end, total)) : '…'}</span></div>
				</div>
			</div>
			{/* Пределы нейросети — только когда ролик в них не помещается: иначе человеку незачем о них знать. */}
			{total != null && total > cap && <p className="sc-motion__limits">Возьмём до {cap} секунд — рамкой выберите нужный кусок</p>}
			{!tooShort && checks.length > 0 && (
				<ul className="sc-motion__checks" aria-label="Что мы проверили">
					{checks.map((c) => (
						<li key={c.text} className={`is-${c.tone}`}>
							{c.tone === 'ok' ? <Check size={13} aria-hidden="true" /> : <AlertTriangle size={13} aria-hidden="true" />}{c.text}
						</li>
					))}
				</ul>
			)}
		</div>
	)
}
